import React from 'react';
import { Box, SpaceBetween, Spinner, Toggle } from '@cloudscape-design/components';
import ToggleRoleButton from 'styles/ToggleRoleButton';
import { Role } from 'types/organizationTypes';
import PERMISSIONS from 'configs/Permissions';

interface RoleSelectorProps {
    roles: Role[];
    selectedRoles: Record<string, boolean>;
    loadingRoles: boolean;
    toggleRole: (role: string) => void;
    selectAll: (type: 'all' | 'none' | 'all manage' | 'all view' | 'all application') => void;
    defaultRoles: string[];
}
const RoleSelector: React.FC<RoleSelectorProps> = ({
    roles,
    selectedRoles,
    loadingRoles,
    toggleRole,
    selectAll,
    defaultRoles,
}) => {
    const sortRoles = (roles: Role[]) => {
        return [...roles].sort((a, b) => {
            // First priority: default roles
            const aIsDefault = defaultRoles.includes(a.name);
            const bIsDefault = defaultRoles.includes(b.name);
            if (aIsDefault && !bIsDefault) return -1;
            if (!aIsDefault && bIsDefault) return 1;

            // Second priority: role type (view before manage)
            const aIsView = a.name.toLowerCase().startsWith('view');
            const bIsView = b.name.toLowerCase().startsWith('view');
            if (aIsView && !bIsView) return -1;
            if (!aIsView && bIsView) return 1;

            // Third priority: manage-members right after view roles
            const aIsManageMembers = a.name === PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS;
            const bIsManageMembers = b.name === PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS;
            if (aIsManageMembers && !bIsManageMembers) return -1;
            if (!aIsManageMembers && bIsManageMembers) return 1;

            // Finally, alphabetical order
            return a.name.localeCompare(b.name);
        });
    };

    const areAllRolesSelected = (type: string) => {
        if (type === 'none') {
            return !roles.some(role => !defaultRoles.includes(role.name) && selectedRoles[role.name]);
        }
        return roles
            .filter(role => type === 'all' ||
                (type === 'all manage' && role.name.startsWith('manage-')) ||
                (type === 'all view' && role.name.startsWith('view-')) ||
                (type === 'all application' && role.name.startsWith(PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS)))
            .every(role => defaultRoles.includes(role.name) || selectedRoles[role.name]);
    };
    const getRoleColor = (role: Role) => {
        if (!role || !role.name) return "gray";
        const lowerName = role.name.toLowerCase();
        if (lowerName.startsWith(PERMISSIONS.ORGANIZATIONS.VIEW_MEMBERS.split('-')[0])) return "lightblue";
        if (lowerName.startsWith(PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS.split('-')[0])) return "darkblue";
        return "gray";
    };

    const handleToggleRole = (roleName: string) => {
        const isSelected = !!selectedRoles[roleName];

        if (roleName === PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS) {
            if (!isSelected) {
                // assigning manage-members → also assign view-members
                toggleRole(PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS);
                if (!selectedRoles[PERMISSIONS.ORGANIZATIONS.VIEW_MEMBERS]) {
                    toggleRole(PERMISSIONS.ORGANIZATIONS.VIEW_MEMBERS);
                }
            } else {
                // removing manage-members → only remove it (keep view-members)
                toggleRole(PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS);
            }
        } else if (roleName === PERMISSIONS.ORGANIZATIONS.VIEW_MEMBERS) {
            if (!isSelected) {
                // assigning view-members normally
                toggleRole(PERMISSIONS.ORGANIZATIONS.VIEW_MEMBERS);
            } else {
                // removing view-members → also remove manage-members if assigned
                toggleRole(PERMISSIONS.ORGANIZATIONS.VIEW_MEMBERS);
                if (selectedRoles[PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS]) {
                    toggleRole(PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS);
                }
            }
        } else {
            // default behavior
            toggleRole(roleName);
        }
    };

    return (
        <div className='role-selector-container'>
            <div className='role-selector'>
                <div className="roles-header">
                    <span className="roles-label">Choose roles: </span>
                    <SpaceBetween direction="horizontal" size="s">
                        <ToggleRoleButton
                            onClick={() => selectAll("all manage")}
                            variant='inline-link'
                            disabled={areAllRolesSelected("all manage")}
                        >
                            all manage
                        </ToggleRoleButton>
                        <ToggleRoleButton
                            onClick={() => selectAll("all view")}
                            variant='inline-link'
                            disabled={areAllRolesSelected("all view")}
                        >
                            all view
                        </ToggleRoleButton>
                        <ToggleRoleButton
                            onClick={() => selectAll("all application")}
                            variant='inline-link'
                            disabled={areAllRolesSelected("all application")}
                        >
                            all application
                        </ToggleRoleButton>
                        <ToggleRoleButton
                            onClick={() => selectAll("all")}
                            variant='inline-link'
                            disabled={areAllRolesSelected("all")}
                        >
                            all
                        </ToggleRoleButton>
                        <ToggleRoleButton
                            onClick={() => selectAll("none")}
                            variant='inline-link'
                            disabled={areAllRolesSelected("none")}

                        >
                            none
                        </ToggleRoleButton>
                    </SpaceBetween>
                </div>

                {loadingRoles ? (
                    <Box textAlign="center" padding="l">
                        <SpaceBetween size="s" direction="vertical" alignItems="center">
                            <Spinner size="large" />
                            <span>Loading roles...</span>
                        </SpaceBetween>
                    </Box>
                ) : roles.length === 0 ? (
                    <Box textAlign="center" color="inherit" padding="l">
                        <SpaceBetween size="s" direction="vertical" alignItems="center">
                            <Box variant="strong">No roles available</Box>
                            <Box variant="p">There are currently no roles defined in the system.</Box>
                        </SpaceBetween>
                    </Box>
                ) : (
                    sortRoles(roles).map((role) => (
                        <div key={role.name} className="role-item">
                            <ul className="role-name-container">
                                <span className="circle" style={{ backgroundColor: getRoleColor(role) }}></span>
                                <li className="role-name">{role.name}</li>
                            </ul>
                            <div className="role-toggle-container">
                                <SpaceBetween direction="horizontal" size="m">
                                    <Box className="role-description-span">{role.description}</Box>
                                    <Toggle
                                        onChange={() => {
                                            if (!defaultRoles.includes(role.name)) {
                                                handleToggleRole(role.name);
                                            }
                                        }}
                                        checked={defaultRoles.includes(role.name) || !!selectedRoles[role.name]}
                                        disabled={defaultRoles.includes(role.name)}
                                    />
                                </SpaceBetween>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default RoleSelector;
